import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subscription} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {buildCpWidgetForm, CpWidgetColumn} from '../../../../../service/domain/cp-widget-column';
import {CpConditionSetSummary} from '../../../../../interface/cp-condition-set-summary.interface';
import {CpConditionSetUsage} from '../../../../../interface/cp-condition-set-usage.interface';
import {CpConditionSetOverview} from '../../../../../interface/cp-condition-set-overview.interface';
import {CpProvisionAssignmentConditionService} from '../../../../../service/ql/cp-provision-assignment-condition.service';
import {
    CP_ALL_DIMENSION_ATTRIBUTES,
    CpProvisionTypeDimensionService
} from '../../../../../service/ql/cp-provision-type-dimension.service';
import {
    CP_CONDITION_DIMENSION_COLUMNS,
    cpConditionModeIcon,
    cpConditionModeLabel,
    cpConditionModeOf,
    cpConditionModeSeverity,
    cpRowValue
} from '../../../../../service/domain/cp-provision-assignment-columns';

/**
 * navItem "Bedingungssätze" of the ProvisionType object component — the named condition sets
 * (`cp.ProvisionAssignmentConditionSet`) of this provision type and their conditions
 * (`cp.ProvisionAssignmentCondition`).
 *
 * ## Why a master-detail inside the object page is correct HERE
 * The set is a child of the COMPLEX `cp.ProvisionType` (edited from its object page, CONFIG-003)
 * and owns exactly ONE configuration child of its own: the condition. The six assignment families
 * point at a set only as a REFERENCE (optional FK, "which contracts"), exactly like K-10 points at
 * a matrix — they are owned by the provision type and maintained on their own navItems. One owned
 * child = SIMPLE, so the master-detail on the same nav point is the prescribed form — the same
 * reading as the navItem "Matrizen" (K-08 -> K-09).
 *
 * ## Layout and widget lifecycle (fe_core_review-workbench, fe_core_widget master-detail)
 * The navItem is a Review Workbench on the shared `mvs-review-workbench` (R1) — it replaced a fixed
 * `col-5` / `col-7` grid that cut off the columns "Verwendungen" and "Status" at 1280 px. Left
 * (`#workbenchList`): the set table (`config.cp.ProvisionAssignmentConditionSet.table`), created ONCE
 * per provision type and never rebuilt on selection, so sorting and scroll survive (R2). Row click
 * selects (`selectionMode = rowSelect`) -> {@link selectedSetId} -> detail (R4). Right
 * (`#workbenchDetail`): the selected set — header with name, alias, counts and the "greift nie"
 * warning, "Verwendet von", delete — and its condition table
 * (`config.cp.ProvisionAssignmentCondition.table`), which is CLEARED and rebuilt per selection (it is
 * filtered on the set id). `[panes]="selectedSetId ? 2 : 1"`: without a selection the set table takes
 * the full width; both modes render the same splitter, so the set table is never remounted (R3).
 * Each pane owns its scroll on both axes; the host measures itself against the viewport
 * (`autoFitViewport`, R5). That is correct in BOTH hosts this component has, because in both the
 * window bottom IS the bottom of the frame the host sits in — it is level 1 in each, never projected
 * into another workbench's pane (R7 does not apply):
 * - full view (`view/full`, routed object page under the app shell);
 * - side view (`view/side`, `?sideNavItem=conditionSets`), rendered in the full-height right
 *   `p-drawer` whose `.p-drawer-content` is the scroll container. Measured at 1280x800 with more
 *   rows than fit: host 192..772 px, drawer content 1..799 px with `scrollHeight == clientHeight`
 *   (no drawer scroll, no clipping) — only the 2rem gap is ~17 px larger than the drawer's own
 *   bottom padding. Auto-fit is therefore bound unconditionally. Should the component ever be
 *   rendered inside a container that does NOT reach the window bottom (a dialog, another
 *   workbench's pane), auto-fit becomes wrong there (R7) and must turn into an input.
 *
 * ## The counts are custom columns
 * "Bedingungen" (total / Einschließen / Ausschließen) and "Verwendungen" are aggregates over the
 * reverse relations; a QL `view` cannot count them. They come from
 * `CpProvisionAssignmentConditionService.getSetOverview` (ONE QL batch, client-side counted) and
 * are rendered into `#cust_` columns of the curated `widgetForm` — the bd proposal-worklist
 * pattern. They are re-read whenever either table reloads, i.e. on entry and after every create,
 * edit or delete. A failed read shows an error with "Erneut laden" instead of zeros — "0" and
 * "nicht verwendet" would be wrong statements about the configuration.
 *
 * ## Delete
 * The backend refuses to delete a set that is still referenced by an assignment or still holds
 * conditions (`ProvisionAssignmentConditionSetOnChange`), with a German sentence naming the
 * families. The explicit "Bedingungssatz löschen" action shows that sentence inline (and the global
 * HTTP interceptor toasts it), so the caseworker learns WHY and WHERE — and the usage list right
 * above the button already shows the blocking assignments before they try.
 *
 * ## Narrow containers: detail STACKED below the list
 * In the side view (right `p-drawer`, 512 px at 1280x800, 768 px at 1920x1080) a side-by-side
 * split leaves the detail pane 169 / 261 px wide — "Bedingungssatz löschen" was clipped resp.
 * overlapped the set name. Below {@link STACK_BELOW_REM} of the component's OWN
 * width the two panes are therefore stacked (list above, detail below) while a set is selected.
 * Driven by a `ResizeObserver` on the host, not by the viewport: the drawer is narrow at every
 * viewport width. `mvs-review-workbench` offers only `stackBelowLg`, a VIEWPORT media query that
 * by its own documentation does not see its container, so the stacked band lives here and does
 * what that band does — a CSS class, never a structural change, so neither pane's embedded view
 * (and with it the set table, R2) is re-created when the layout flips. See the SCSS.
 *
 * ## Jump target
 * The assignment tabs' condition preview jumps here with {@link focusConditionSetId}; the set is
 * then selected without a click and {@link onFocusConditionSetConsumed} tells the host to drop the
 * one-shot jump target.
 */
@Component({
    selector: 'cp-provision-type-condition-set',
    templateUrl: './cp-provision-type-condition-set.component.html',
    styleUrls: ['./cp-provision-type-condition-set.component.scss'],
    standalone: false
})
export class CpProvisionTypeConditionSetComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    /** Stable widget aliases (CONFIG-011) — identical across every selection. */
    static readonly cAliasSet = 'config.cp.ProvisionAssignmentConditionSet.table';
    static readonly cAliasCondition = 'config.cp.ProvisionAssignmentCondition.table';

    /**
     * Container width (rem) below which the selected set's detail is stacked below the list.
     * Measured: the side drawer is 512 px at 1280x800 and 768 px at 1920x1080; side by side the
     * detail pane gets 169 px resp. 261 px there and the header's delete button overlaps the set
     * name. 60rem (840 px at the 14 px root) stacks both drawer widths and keeps the full object view
     * side by side from a 1280 px window on (host ~1160 px, detail ~410 px).
     */
    static readonly STACK_BELOW_REM = 60;

    /** fieldIds of the custom columns (`#cust_<fieldId>` in the widgetForm). */
    readonly conditionCountFieldId = 'conditionCount';
    readonly usageCountFieldId = 'usageCount';
    readonly conditionModeFieldId = 'conditionMode';

    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Set to select on entry — the jump from an assignment tab. */
    @Input() focusConditionSetId: number | null;

    /** The focus set was selected — the host resets {@link focusConditionSetId}. */
    @Output() onFocusConditionSetConsumed = new EventEmitter<void>();

    setWidget: WidgetData;
    setImportContext: DtoImportObjectContext;

    conditionWidget: WidgetData;
    conditionImportContext: DtoImportObjectContext;

    /** Selected set; `null` = nothing selected, the right side shows the hint. */
    selectedSetId: number | null = null;

    /** Name/alias of the selected set as the row delivered them (before the summaries answer). */
    selectedSetName: string | null = null;
    selectedSetAlias: string | null = null;

    /**
     * Counts per set id; `countsLoaded` separates "0" from "not known yet", `countsFailed` from
     * "could not be read".
     */
    summaries = new Map<number, CpConditionSetSummary>();
    usages = new Map<number, CpConditionSetUsage[]>();
    countsLoaded: boolean;
    countsFailed: boolean;

    /** Delete action state. */
    deleteBusy: boolean;
    deleteError: string | null = null;

    /** The host is narrower than {@link STACK_BELOW_REM} — measured by {@link observeWidth}. */
    narrowContainer: boolean = false;

    private resizeObserver: ResizeObserver | null = null;

    /** Dimensions filled in any condition of THIS provision type — `null` until probed. */
    private usedDimensionAttributes: string[] = null;

    private countsSubscription: Subscription;
    private dimensionSubscription: Subscription;
    private deleteSubscription: Subscription;

    constructor(private conditionService: CpProvisionAssignmentConditionService,
                private dimensionService: CpProvisionTypeDimensionService,
                private coreService: MvsCoreService,
                private messageService: MvsMessageService,
                private confirmationService: ConfirmationService,
                private elementRef: ElementRef<HTMLElement>,
                private ngZone: NgZone) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    ngAfterViewInit(): void {
        this.observeWidth();
    }

    /**
     * Stack only while a set is selected: without one the detail pane is collapsed (`panes = 1`)
     * and the list already has the full width.
     */
    get stacked(): boolean {
        return this.narrowContainer && !!this.selectedSetId;
    }

    /**
     * Watch the host's own width. Runs outside the Angular zone and re-enters only when the
     * narrow/wide verdict actually flips, so a drawer resize costs no change detection per frame.
     * The rem threshold is resolved against the CURRENT root font size on every callback — the
     * UI-scale control rewrites it at runtime.
     */
    private observeWidth(): void {

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const host = this.elementRef.nativeElement;

        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => {
                const width = host.getBoundingClientRect().width;

                // A hidden host (inactive tab, closed drawer) measures 0 - keep the last verdict.
                if (!width) {
                    return;
                }

                const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
                const narrow = width < CpProvisionTypeConditionSetComponent.STACK_BELOW_REM * rootFontPx;

                if (narrow !== this.narrowContainer) {
                    this.ngZone.run(() => this.narrowContainer = narrow);
                }
            });
            this.resizeObserver.observe(host);
        });
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.cancelSubscriptions();
        this.clearSelection();
        this.summaries = new Map();
        this.usages = new Map();
        this.countsLoaded = false;
        this.countsFailed = false;
        this.usedDimensionAttributes = null;

        if (!this.provisionTypeId) {
            this.setWidget = null;
            this.setImportContext = null;
            this.initialized = true;
            return;
        }

        // The counts follow the set table's first load (onSetDataLoaded) — not read here as well.
        this.buildSetWidget();
        this.probeDimensions();

        if (this.focusConditionSetId) {
            this.selectFocusSet();
        }

        this.initialized = true;
    }

    // --- level 1: the sets ------------------------------------------------------------------- //

    /** The set table of this provision type — built ONCE per provision type. */
    private buildSetWidget(): void {

        const query: QlQueryDto = {
            name: 'config.cp.ProvisionAssignmentConditionSet',
            type: 'view',
            start: {type: 'entity', name: 'cp.ProvisionAssignmentConditionSet', as: 'e'},
            fixedFilters: [
                FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, this.provisionTypeId)
            ],
            sortings: [new Sorting('e.name', true), new Sorting('e.id', true)]
        };

        const columns: CpWidgetColumn[] = [
            {field: 'e.name', label: 'Name'},
            {field: 'e.alias', label: 'Alias'},
            {field: '#cust_' + this.conditionCountFieldId, label: 'Bedingungen'},
            {field: '#cust_' + this.usageCountFieldId, label: 'Verwendungen'},
            {field: 'e.entityStatus', label: 'Status'}
        ];

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Condition sets (cp.ProvisionAssignmentConditionSet, "Bedingungssätze") of EXACTLY
         *     this provision type — the master of the navItem. Row click selects the set and shows
         *     its conditions, usages and the delete action on the right. Columns: name, alias, the
         *     condition counts (total / Einschließen / Ausschließen) and the number of assignments
         *     using the set, both as #cust_ columns fed from client-side counted QL queries, and
         *     status. A new set gets its mandatory FK provisionType pre-filled.
         */
        this.setWidget = WidgetFactory.createWidgetTableQl(
            CpProvisionTypeConditionSetComponent.cAliasSet,
            'Bedingungssätze',
            query,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm(columns))
        );
        this.setWidget.setParamValue('selectionMode', 'rowSelect');
        this.setWidget.noDataText = 'Noch keine Bedingungssätze angelegt';

        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeDtoId', fieldValue: this.provisionTypeId}
        ];
        this.setWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.setImportContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId)]);
    }

    /** Counts and usages of every set of the provision type — one batched QL request. */
    loadCounts(): void {

        if (this.countsSubscription) {
            this.countsSubscription.unsubscribe();
        }

        // A reload keeps the previous counts on screen until the answer replaces them.
        this.countsSubscription = this.conditionService.getSetOverview(this.provisionTypeId)
            .subscribe({
                next: overview => this.applyOverview(overview),
                error: () => {
                    this.summaries = new Map();
                    this.usages = new Map();
                    this.countsLoaded = false;
                    this.countsFailed = true;
                }
            });
    }

    /** "Erneut laden" of the count error state. */
    onRetryCounts(): void {
        this.countsFailed = false;
        this.loadCounts();
    }

    private applyOverview(overview: CpConditionSetOverview): void {

        const summaryMap = new Map<number, CpConditionSetSummary>();
        (overview?.summaries ?? []).forEach(summary => summaryMap.set(summary.id, summary));

        const usageMap = new Map<number, CpConditionSetUsage[]>();
        (overview?.usages ?? []).forEach(usage => {
            const list = usageMap.get(usage.setId) ?? [];
            list.push(usage);
            usageMap.set(usage.setId, list);
        });

        this.summaries = summaryMap;
        this.usages = usageMap;
        this.countsFailed = false;
        this.countsLoaded = true;
    }

    /** The set table (re)loaded — after create, edit or delete. Counts follow. */
    onSetDataLoaded(): void {
        this.loadCounts();
    }

    onSetSelect(event: ObjectIdentifier | ObjectIdentifierData): void {
        const data = (event as ObjectIdentifierData)?.data;
        const setId = event?.objectId ? +event.objectId : +cpRowValue(data, 'e.id');
        if (!setId) {
            return;
        }
        this.selectSet(setId, cpRowValue(data, 'e.name'), cpRowValue(data, 'e.alias'));
    }

    /**
     * Select the jump target {@link focusConditionSetId} and report it consumed. The report is
     * deferred past the running change detection: the host resets its binding in response, which
     * would otherwise change an already checked expression (NG0100).
     */
    private selectFocusSet(): void {
        if (+this.focusConditionSetId !== this.selectedSetId) {
            this.selectSet(+this.focusConditionSetId, null, null);
        }
        queueMicrotask(() => this.onFocusConditionSetConsumed.emit());
    }

    /** Select a set: clear the detail first, then build it for the new set. */
    private selectSet(setId: number, name: string | null, alias: string | null): void {
        this.conditionWidget = null;
        this.conditionImportContext = null;
        this.deleteError = null;
        this.selectedSetId = setId;
        this.selectedSetName = name;
        this.selectedSetAlias = alias;
        this.buildConditionWidget();
    }

    private clearSelection(): void {
        this.selectedSetId = null;
        this.selectedSetName = null;
        this.selectedSetAlias = null;
        this.conditionWidget = null;
        this.conditionImportContext = null;
        this.deleteError = null;
        this.deleteBusy = false;
    }

    /** Close the detail. */
    onDetailClose(): void {
        this.clearSelection();
    }

    // --- counts for the custom columns and the detail header ---------------------------------- //

    /** Summary of the set of a table row (or of the selected set when `object` is a number). */
    summaryOf(objectOrId: any): CpConditionSetSummary | null {
        const id = typeof objectOrId === 'number' ? objectOrId : +cpRowValue(objectOrId, 'e.id');
        return id ? (this.summaries.get(id) ?? null) : null;
    }

    usagesOf(objectOrId: any): CpConditionSetUsage[] {
        const id = typeof objectOrId === 'number' ? objectOrId : +cpRowValue(objectOrId, 'e.id');
        return id ? (this.usages.get(id) ?? []) : [];
    }

    /** Header name of the selected set — the summary wins, the clicked row is the fallback. */
    get selectedName(): string {
        return this.summaryOf(this.selectedSetId)?.name || this.selectedSetName || '';
    }

    get selectedAlias(): string {
        return this.summaryOf(this.selectedSetId)?.alias || this.selectedSetAlias || '';
    }

    // --- level 2: the conditions of the selected set ------------------------------------------ //

    /**
     * Probe which dimensions the conditions of this provision type use — the column reduction of
     * 04-frontend.md §6 now lives on the condition table. Scoped to the TYPE, not to the set, so
     * the column set does not jump while the user clicks through the sets. Runs on entry and again
     * after every load of the condition table ({@link onConditionDataLoaded}).
     */
    private probeDimensions(): void {

        if (this.dimensionSubscription) {
            this.dimensionSubscription.unsubscribe();
        }

        this.dimensionSubscription = this.dimensionService
            .getUsedDimensionAttributes(this.provisionTypeId)
            .subscribe({
                next: attributes => this.applyUsedDimensions(attributes ?? CP_ALL_DIMENSION_ATTRIBUTES),
                error: () => this.applyUsedDimensions(CP_ALL_DIMENSION_ATTRIBUTES)
            });
    }

    /**
     * Take a probe answer. The condition table is (re)built only when it does not exist yet or when
     * the used dimensions actually CHANGED — a re-probe after every load of the condition table
     * therefore settles after one rebuild and cannot loop.
     */
    private applyUsedDimensions(attributes: string[]): void {

        const changed = !this.usedDimensionAttributes
            || this.usedDimensionAttributes.join('|') !== attributes.join('|');

        this.usedDimensionAttributes = attributes;

        if (this.selectedSetId && (!this.conditionWidget || changed)) {
            this.conditionWidget = null;
            this.buildConditionWidget();
        }
    }

    /**
     * The conditions of the selected set. Waits for the dimension probe so the table renders once
     * with its final column set.
     */
    private buildConditionWidget(): void {

        const setId = this.selectedSetId;

        if (!setId || !this.usedDimensionAttributes) {
            this.conditionWidget = null;
            return;
        }

        const columns: CpWidgetColumn[] = [
            {field: 'e.orderPriority', label: 'Priorität'},
            {field: '#cust_' + this.conditionModeFieldId, label: 'Wirkung'},
            ...CP_ALL_DIMENSION_ATTRIBUTES
                .filter(attribute => this.usedDimensionAttributes.includes(attribute))
                .map(attribute => CP_CONDITION_DIMENSION_COLUMNS[attribute])
                .filter((column): column is CpWidgetColumn => !!column),
            {field: 'e.description', label: 'Beschreibung'}
        ];

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Conditions (cp.ProvisionAssignmentCondition, "Bedingungen") of the SELECTED
         *     condition set, in evaluation order (orderPriority). Shows the effect as a coloured
         *     tag (Einschließen green / Ausschließen red) and only the contract dimensions used by
         *     any condition of this provision type, with their names joined. A new condition gets
         *     both mandatory FKs — the set and the provision type — pre-filled.
         */
        this.conditionWidget = WidgetFactory.createWidgetTableQl(
            CpProvisionTypeConditionSetComponent.cAliasCondition,
            'Bedingungen',
            this.conditionService.buildConditionsOfSetQuery(setId),
            WidgetDataParam.create('widgetForm', buildCpWidgetForm(columns))
        );
        this.conditionWidget.noDataText = 'Keine Bedingungen — dieser Bedingungssatz greift nie';

        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionAssignmentConditionSetDtoId', fieldValue: setId},
            {fieldName: 'provisionTypeDtoId', fieldValue: this.provisionTypeId}
        ];
        this.conditionWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.conditionImportContext = new DtoImportObjectContext([
            new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId),
            new DtoImportObjectContextEntry('cp.ProvisionAssignmentConditionSet', setId)
        ]);
    }

    /**
     * The condition table (re)loaded — after a condition was created, edited or deleted. The counts
     * of the set may have changed, and so may the dimensions in use (a first condition on a product
     * adds the "Produkt" column), hence the re-probe.
     */
    onConditionDataLoaded(): void {
        this.loadCounts();
        this.probeDimensions();
    }

    conditionModeLabel(object: any): string {
        return cpConditionModeLabel(cpConditionModeOf(cpRowValue(object, 'e.conditionMode')));
    }

    conditionModeSeverity(object: any): 'success' | 'danger' | 'secondary' {
        return cpConditionModeSeverity(cpConditionModeOf(cpRowValue(object, 'e.conditionMode')));
    }

    conditionModeIcon(object: any): string {
        return cpConditionModeIcon(cpConditionModeOf(cpRowValue(object, 'e.conditionMode')));
    }

    // --- delete --------------------------------------------------------------------------------- //

    /**
     * Delete the selected set after confirmation. A refusal of the backend (set still used or
     * still holding conditions) is shown inline with the backend's own German sentence.
     */
    onDeleteSet(): void {

        const setId = this.selectedSetId;
        if (!setId || this.deleteBusy) {
            return;
        }

        // DOCUMENTED DEVIATION from UX7 (Font Awesome only): ConfirmationService.confirm()'s `icon`
        // renders through PrimeNG's own ConfirmDialog template, which expects a `pi` class — same
        // exception as cp-provision-agent-change-dialog.component.ts.
        this.confirmationService.confirm({
            header: 'Bedingungssatz löschen',
            message: 'Soll der Bedingungssatz „' + this.selectedName + '“ gelöscht werden?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Löschen',
            rejectLabel: 'Abbrechen',
            accept: () => this.executeDelete(setId)
        });
    }

    private executeDelete(setId: number): void {

        this.deleteBusy = true;
        this.deleteError = null;

        this.deleteSubscription = this.coreService.getCrudService('cp.ProvisionAssignmentConditionSet')
            .delete(setId)
            .subscribe({
                next: () => {
                    this.deleteBusy = false;
                    this.messageService.showSuccessMessage('Bedingungssatz', 'Der Bedingungssatz wurde gelöscht.');
                    // The crud service broadcasts the deletion; the set table reloads itself and
                    // with it the counts (onSetDataLoaded).
                    this.clearSelection();
                },
                error: error => {
                    this.deleteBusy = false;
                    this.deleteError = this.readBackendMessage(error)
                        ?? 'Der Bedingungssatz konnte nicht gelöscht werden.';
                }
            });
    }

    /**
     * The German refusal sentence out of an error response. `CoreApiResponseDto` carries it in
     * `data`; other error shapes in `message` / the first `errors[].message`.
     */
    private readBackendMessage(error: any): string | null {
        const body = error?.error;
        const candidates = [
            body?.data,
            body?.message,
            Array.isArray(body?.errors) ? body.errors[0]?.message : null,
            typeof body === 'string' ? body : null
        ];
        const text = candidates.find(value => typeof value === 'string' && value.trim().length);
        return text ?? null;
    }

    private cancelSubscriptions(): void {
        [this.countsSubscription, this.dimensionSubscription, this.deleteSubscription]
            .forEach(subscription => subscription?.unsubscribe());
        this.countsSubscription = null;
        this.dimensionSubscription = null;
        this.deleteSubscription = null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId']) {
            this.refreshComponent();
            return;
        }
        if (changes['focusConditionSetId'] && this.focusConditionSetId) {
            this.selectFocusSet();
        }
    }

    ngOnDestroy(): void {
        this.cancelSubscriptions();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }

}
